import { Injectable } from '@angular/core';
import {
  differenceInDays,
  differenceInMinutes,
  format,
  formatDistance,
  formatDistanceToNow,
  parseISO,
} from 'date-fns';
import { Dictionary } from 'lodash';
import { map } from 'rxjs';
import { BaseService } from 'src/app/helpers/base.service';
import { iEmployeeAttendanceSummaryRequest, iEmployeeAttendanceSummaryResponse } from 'src/app/helpers/Interfaces/attendance';
import {
  iCommentsRequest,
  iCommentsResult,
} from 'src/app/helpers/Interfaces/comments';
import {
  iPaginationRequest,
  iPaginationResult,
} from 'src/app/helpers/Interfaces/pagination';
import {
  iTaskDashboardResponse,
  iTaskDatesReq,
  iTaskList,
  iTaskListDto,
  iTaskReq,
  iTaskResult,
  iTaskResultDto,
  iTaskViewReq,
  iTaskViewRes,
} from 'src/app/helpers/Interfaces/task';
import {
  iTimeTrackingEmp,
  iTimeTrackingResponse,
  iTimeTrackingResponseDto,
} from 'src/app/helpers/Interfaces/time-tracking';
import { iWPTask } from 'src/app/helpers/Interfaces/weekly-planner';
import { environment } from 'src/environments/environment.development';

@Injectable({
  providedIn: 'root',
})
export class TasksService extends BaseService {
  private _getAllTasks = `${environment.base_url}/api/Task/GetAllWithRelationship`;
  private _getAllTasks1 = `${environment.base_url}/api/Task/GetAllWithRelationshipWithFilteration`;
  private _getConversation = `${environment.base_url}/api/Conversation/GetbyTask`;
  private _getConversationById = `${environment.base_url}/api/Conversation/GetById`;
  private _addConversation = `${environment.base_url}/api/Conversation/Add`;
  private _addConversationFiles = `${environment.base_url}/api/ConversationAttachment/AddRangeFiles`;
  private _getTaskById = `${environment.base_url}/api/Task/GetByIdWithRelationship`;
  private _getTasksByEmployee = `${environment.base_url}/api/Task/GetByEmployee`;
  private _getTeamTasksByEmployee = `${environment.base_url}/api/Task/GetReportsToByEmployee`;
  private _getTasksByEmployeeRelation = `${environment.base_url}/api/Task/GetByEmployeeWithRelationship`;
  private _addTask = `${environment.base_url}/api/Task/Add`;
  private _addRangeTask = `${environment.base_url}/api/Task/AddRange`;
  private _updateTask = `${environment.base_url}/api/Task/Update`;
  private _addTaskStatus = `${environment.base_url}/api/TaskStatus/Add`;
  private _addTaskFiles = `${environment.base_url}/api/TaskFiles/AddRangeFiles`;
  private _removeTask = `${environment.base_url}/api/Task/Remove`;
  private _removeTaskFIle = `${environment.base_url}/api/TaskFiles/Remove`;
  private _getStatusHistoryByTask = `${environment.base_url}/api/TaskStatus/GetByTaskId`;
  private _markAs = `${environment.base_url}/api/Task/MarkAsRead`;
  private _updateDates = `${environment.base_url}/api/Task/UpdateDates`;

  // EmployeeTask
  private _getEmployeeTaskByTask = `${environment.base_url}/api/EmployeeTask/GetByTaskId`;
  private _removeEmployeeTask = `${environment.base_url}/api/EmployeeTask/Remove`;
  private _addEmployeeTask = `${environment.base_url}/api/EmployeeTask/Add`;
  private _checkLoad = `${environment.base_url}/api/EmployeeTask/CheckLoad`;
  private _getTasksByEmployeeRange = `${environment.base_url}/api/EmployeeTask/GetByEmployeeIds`;
  private _getEmployeeTaskStatus = `${environment.base_url}/api/projectandemployeedashboard/getallemployeesstatuswisetaskcount`;
  // TimeTracking
  private _getTimeRecordsByEmp = `${environment.base_url}/api/TimeTracking/GetByEmployee`;
  private _getTimeRecordsByEmpAndStatus = `${environment.base_url}/api/TimeTracking/GetByEmployeeAndStatus`;
  private _getTimeRecordsByTask = `${environment.base_url}/api/TimeTracking/GetByTask`;
  private _addTimeTracking = `${environment.base_url}/api/TimeTracking/Add`;
  private _updateTimeTracking = `${environment.base_url}/api/TimeTracking/Update`;
  private _deleteTimeTracking = `${environment.base_url}/api/TimeTracking/Remove`;
  private _getLastTimeTrack = `${environment.base_url}/api/TimeTracking/GetLast`;
  private _getTimeTrackByEmpAndTask = `${environment.base_url}/api/TimeTracking/GetByTaskAndEmplLast`;
  private _getAllDue = `${environment.base_url}/api/Task/GetAllDue`;
  private _getEmpTimeTracking = `${environment.base_url}/api/Attendance/getEmployeeTimeTrackingSummary`;

  // Daily Reports

  private readonly _getDailyReports = `${environment.base_url}/api/DailyReport`;

  public GetDailyReports(payload: {
    pageNumber: number;
    pageSize: number;
    skipPagination: boolean;
    fromDate: string;
    toDate: string;
    employeeId: string | null;
    employeeIds: string[];
    username: string;
    taskTitle: string;
    teamId: string | null;
    sortBy: string;
    isDescending: boolean;
  }) {
    const normalizedPayload = {
      ...payload,
      employeeId: payload.employeeId || null,
      employeeIds: Array.isArray(payload.employeeIds) ? payload.employeeIds : [],
      teamId: payload.teamId || null,
    };

    return this._http.post<any>(
      this._getDailyReports,
      {
        ...normalizedPayload,
        request: normalizedPayload,
      },
      {
        responseType: 'json',
      }
    );
  }

  public GetDailyReportByEmployee(
    employeeId: string,
    fromDate?: string,
    toDate?: string
  ) {
    const params: Record<string, string> = {};
    if (fromDate) params['fromDate'] = fromDate;
    if (toDate) params['toDate'] = toDate;

    return this._http.get<any>(`${this._getDailyReports}/${employeeId}`, {
      responseType: 'json',
      params,
    });
  }

  private _getOverdueIncompleteTasks = `${environment.base_url}/api/task/getoverdueincompletetasks`;

  public GetOverdueIncompleteTasks(payload: any) {
    return this._http.post<any>(this._getOverdueIncompleteTasks, payload, {
      responseType: 'json',
    });
  }

  // Comments

  private readonly _addComments = `${environment.base_url}/api/Comments/Add`;
  private readonly _updateComments = `${environment.base_url}/api/Comments/Update`;

  private readonly _getForManagementDashboard = `${environment.base_url}/api/Task/GetForManagementDashboard`;
  private readonly _getForNewTaskView = `${environment.base_url}/api/Task/GetForNewTaskView`;
  private readonly _getTasksInProgressByEmployee = `${environment.base_url}/api/task/gettasksinprogressbyemployee`;
  private readonly _getForPendingTaskView = `${environment.base_url}/api/Task/GetOverDueIncompleteTasks`;
  private readonly _getForNewTaskViewTeam = `${environment.base_url}/api/EmployeeTask/GetReportsToTasksByEmployeeIdWithRelationship`;

  private readonly _addTaskView = `${environment.base_url}/api/TaskView/Add`;
  private readonly _updateTaskView = `${environment.base_url}/api/TaskView/Update`;
  private readonly _deleteTaskView = `${environment.base_url}/api/TaskView/Remove`;
  private readonly _getAllTaskView = `${environment.base_url}/api/TaskView/GetAll`;
  private readonly _getTaskViewById = `${environment.base_url}/api/TaskView/GetById`;
  private readonly _getTaskViewByEmp = `${environment.base_url}/api/TaskView/GetByEmployeeId`;

  private readonly _getTaskDescription = `${environment.ai_url}/task_description_rephrase/validate-and-paraphrase`;
  private readonly _generateTasks = `${environment.ai_url}/file_task_creator-osquare/create-tasks`;

  private _employee: any;
  private _role: any;
  // private _empType: any;

  constructor() {
    super();
    this._dataServ.employee$.subscribe((v) => {
      this._employee = v;
    });
    this._dataServ.role$.subscribe((v) => {
      this._role = v;
    });
    // this._empType = _dataServ.empTypeValue;
    // this._showModalSource = new BehaviorSubject<
    //   Nullable<'init' | 'attendance'>
    // >(null);
    // this.showModal$ = this._showModalSource.asObservable();
  }

  private processFiles(files: any[]): any[] {
    return files.map((file) => ({
      ...file,
      name: file.fileName ?? file.name,
      size: `(${((file.fileSize ?? file.size) / 1024)
        .toFixed()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')} KB)`,
      filePath: `${file.filePath}`,
    }));
  }
  private transformArray(originalArray: any[]): any[] {
    console.clear();
    const transformedArray: any[] = [];
    originalArray.forEach((item) => {
      const transformed: any = {
        id: item.id,
        title: item.title,
        createdDate: item.createdDate,
        priority: item.priority,
        employees: item.employees,
        progress: item.progress,
      };

      const lastStatus: any = {
        statusId: item.lastTaskStatus.statusId,
        status: item.lastTaskStatus.status,
        color: item.lastTaskStatus.color,
        collapsed: true,
        active: false,
      };
      const index = transformedArray.findIndex(
        (x) => x.statusId == lastStatus.statusId
      );

      if (!~index) {
        lastStatus.tasks = [transformed];
        transformedArray.push(lastStatus);
      } else {
        transformedArray[index].tasks.push(transformed);
      }
    });

    return transformedArray;
  }
  private processTask(res: iTaskResult): iTaskResultDto {
    const task: iTaskResultDto = {
      ...res,
      des: undefined,
      createdAt: undefined,
      assignedDate: undefined,
      rDueDate: null,
      rStartDate: null,
      progress: '',
    };
    task.des = `${task.description || ''}${task.description && task.body ? ' - ' : ''
      }${task.body || ''}`;
    task.createdAt = formatDistance(
      parseISO(task.createdDate as string),
      new Date(),
      {
        addSuffix: true,
        includeSeconds: true,
      }
    );
    task.assignedDate = format(
      parseISO(task.createdDate as string),
      'MMM do, yyyy p'
    );
    task.createdDate = format(
      parseISO(task.createdDate as string),
      'MMM do, yyyy'
    );

    task.files = this.processFiles(task.files);
    if (task.updateDate) {
      task.updateDate = format(parseISO(task.updateDate), 'MMM do, yyyy p');
    }
    if (task.employees && task.employees.length > 0) {
      task.employees.forEach((x: any) => {
        x.profilePic = `${environment.base_url}/${x.profilePic}`;
        x.name = `${x.firstName} ${x.lastName}`;
      });
    }
    if (task.subTask && task.subTask.length > 0) {
      task.subTask.forEach((sub: any) => {
        if (sub.employees && sub.employees.length > 0) {
          sub.employees.forEach((emp: any) => {
            emp.profilePic = `${environment.base_url}/${emp.profilePic}`;
            emp.name = `${emp.firstName} ${emp.lastName}`;
          });
        }
      });
    }
    const hasSubTasks = task.subTask && task.subTask.length > 0;
    const isCompletedStatus = (statusStr: string) => {
      const s = (statusStr || '').toLowerCase().replace(/[\s-_]/g, '');
      return s === 'completed' || s === 'done';
    };
    const isParentCompleted = [
      'completed',
      'fix to be tested',
      'fixed tested',
      'as designed',
    ].includes((task.lastTaskStatus?.status || '').toLowerCase().trim());

    if (hasSubTasks) {
      if (isParentCompleted) {
        task.progress = (100).toFixed(2);
      } else {
        const parentEtr = Number(task.etr) || 0;
        const totalSubtaskHours = task.subTask.reduce(
          (sum: number, x: any) => sum + (Number(x.etr) || 0),
          0
        );
        const completedSubtasks = task.subTask.filter((x: any) => {
          const raw =
            typeof x.status === 'string'
              ? x.status
              : x.status?.status || x.status?.name || x.status?.title || '';
          return isCompletedStatus(raw);
        });

        const totalHours = parentEtr > 0 ? parentEtr : totalSubtaskHours;

        if (totalHours > 0) {
          const completedHours = completedSubtasks.reduce(
            (sum: number, x: any) => sum + (Number(x.etr) || 0),
            0
          );
          task.progress = Math.min(
            100,
            (completedHours / totalHours) * 100
          ).toFixed(2);
        } else {
          task.progress = (
            (completedSubtasks.length / task.subTask.length) *
            100
          ).toFixed(2);
        }
      }
    } else {
      task.progress = isParentCompleted ? (100).toFixed(2) : (0).toFixed(2);
    }

    return task;
  }
  private processCon(con: any): any {
    con.des = `${con.description || ''}${con.description && con.body ? ' - ' : ''
      }${con.body || ''}`;
    if (con.employeeDetail) {
      con.createdBy = {
        email: con.employeeDetail.contactPersonEmail,
        name: con.employeeDetail.name,
      };
    } else if (con.employee) {
      con.createdBy = {
        email: con.employee.email,
        name: `${con.employee.firstName} ${con.employee.lastName}`,
      };
    }

    con.createdAt = formatDistance(parseISO(con.createdDate), new Date(), {
      addSuffix: true,
      includeSeconds: true,
    });
    con.createdDate = format(parseISO(con.createdDate), 'MMM do, yyyy');
    con.files = this.processFiles(con.conversationAttachments);
    return con;
  }

  updateDates(dates: iTaskDatesReq[]) {
    return this._http.patch(this._updateDates, dates, {
      responseType: 'json',
    });
  }
  public GetAllTasks() {
    // console.log(this._role);

    return this._http
      .get<any[]>(
        this._role == 'employee' && this._dataServ.hasPermission('H1008')
          ? `${this._getTasksByEmployee}?id=${this._employee.id}`
          : this._getAllTasks,
        { responseType: 'json' }
      )
      .pipe(
        map((tasks) => tasks.map((task) => this.processTask(task)).reverse())
      );
  }
  public GetAllTasks1(payload: iPaginationRequest) {
    return this._http
      .post<iPaginationResult<iTaskResult>>(this._getAllTasks1, payload, {
        responseType: 'json',
      })
      .pipe(
        map((res) => ({
          ...res,
          data: res.data.map((task: any) => this.processTask(task)),
        }))
      );
  }
  public GetAllTasks2(self: boolean) {
    return this._http
      .get<any[]>(
        self
          ? `${this._getTasksByEmployee}?id=${this._employee.id}`
          : this._dataServ.employeeValue?.dataVisibility === 'Subordinates Only'
            ? `${this._getTeamTasksByEmployee}?id=${this._employee.id}`
            : this._getAllTasks,
        // this._dataServ.hasPermission('my-tasks', '')
        //   ? `${this._getTasksByEmployee}?id=${this._employee.id}`
        //   : this._getAllTasks,
        {
          responseType: 'json',
        }
      )
      .pipe(map((tasks) => tasks.map((task: any) => this.processTask(task))))
      .pipe(map((tasks) => this.transformArray(tasks)));
  }
  public GetTaskById(id: string) {
    return this._http
      .get<iTaskResult>(`${this._getTaskById}?Id=${id}`)
      .pipe(map((task) => this.processTask(task)));
  }
  public GetTasksByEmployeeId(id: string) {
    return this._http
      .get<any[]>(`${this._getTasksByEmployee}?Id=${id}`, {
        responseType: 'json',
      })
      .pipe(map((tasks) => tasks.map((task) => this.processTask(task))));
  }

  public AddTask(data: any) {
    const payload = { ...data };
    payload.etr = payload.etr.toLocaleString();
    if (data.dueDate)
      payload.dueDate = new Date(data.dueDate)
        .toISOString()
        .replace(/[Zz]/g, '');
    if (data.afterDue)
      payload.afterDue = new Date(data.afterDue)
        .toISOString()
        .replace(/[Zz]/g, '');
    if (data.overDue)
      payload.overDue = new Date(data.overDue)
        .toISOString()
        .replace(/[Zz]/g, '');
    if (data.startDate)
      payload.startDate = new Date(data.startDate)
        .toISOString()
        .replace(/[Zz]/g, '');
    return this._http.post<any>(this._addTask, payload, {
      responseType: 'json',
    });
  }
  public AddTaskRaw(data: any) {
    const payload = { ...data };
    return this._http.post<any>(this._addTask, payload, {
      responseType: 'json',
    });
  }
  public AddRangeTask(payload: any[]) {
    return this._http.post<any[]>(this._addRangeTask, payload, {
      responseType: 'json',
    });
  }
  public UpdateTask(data: any) {
    const payload = { ...data };
    payload.etr = payload.etr.toLocaleString();
    payload.dueDate = new Date(data.dueDate).toISOString().replace(/[Zz]/g, '');
    payload.afterDue = new Date(data.afterDue)
      .toISOString()
      .replace(/[Zz]/g, '');
    payload.overDue = new Date(data.overDue).toISOString().replace(/[Zz]/g, '');
    payload.startDate = new Date(data.startDate)
      .toISOString()
      .replace(/[Zz]/g, '');
    return this._http.put<any>(this._updateTask, payload, {
      responseType: 'json',
    });
  }
  public UpdateTaskRaw(data: any) {
    const payload = { ...data };
    return this._http.put<any>(this._updateTask, payload, {
      responseType: 'json',
    });
  }
  public AddTaskStatus(payload: any) {
    return this._http
      .post(this._addTaskStatus, payload, {
        responseType: 'json',
      })
      .pipe(map(this.processHistoryStatus));
  }
  public GetStatusHistoryByTask(id: string) {
    return this._http
      .get<Array<any>>(`${this._getStatusHistoryByTask}?Id=${id}`)
      .pipe(
        map((statuses) => statuses.map(this.processHistoryStatus).reverse())
      );
  }
  private processHistoryStatus(status: any) {
    status.changedBy = status.employeeId
      ? status.employee
      : status.employeeId
        ? status.employee
        : 'System';
    status.changedAt = format(
      parseISO((status.createdDate as string).replace(/[zZ]/g, '')),
      'MMM do, yyyy p'
    );
    return status;
  }
  public AddTaskFiles(Files: FormData) {
    return this._http
      .post<any>(`${this._addTaskFiles}`, Files, {
        responseType: 'json',
      })
      .pipe(map((files) => this.processFiles(files)));
  }

  public DeleteTask(id: any, skipGlobalErrorToast = false) {
    const headers: any = {};
    if (skipGlobalErrorToast) {
      headers['skipGlobalErrorToast'] = 'true';
    }
    return this._http.delete(`${this._removeTask}?id=${id}`, { headers });
  }
  public DeleteTaskFile(id: string) {
    return this._http.delete(`${this._removeTaskFIle}?id=${id}`);
  }
  public GetConversation(taskId: string) {
    return this._http
      .get<any[]>(`${this._getConversation}?Id=${taskId}`, {
        responseType: 'json',
      })
      .pipe(
        map((tasks) => tasks.map((task) => this.processCon(task)).reverse())
      );
  }
  public AddConversation(payload: any) {
    return this._http.post<any>(this._addConversation, payload, {
      responseType: 'json',
    });
  }
  public AddConversationFiles(payload: any) {
    return this._http.post<any>(this._addConversationFiles, payload, {
      responseType: 'json',
    });
  }
  public GetConversationById(id: string) {
    return this._http
      .get<any>(`${this._getConversationById}?id=${id}`)
      .pipe(map((task) => this.processCon(task)));
  }
  public MarkAs(payload: any) {
    return this._http.put(this._markAs, payload, { responseType: 'json' });
  }

  // EmployeeTask
  public GetEmployeeTaskByTask(taskId: string) {
    return this._http.get<any[]>(
      `${this._getEmployeeTaskByTask}?id=${taskId}`,
      {
        responseType: 'json',
      }
    );
  }
  public RemoveEmployeeTask(empTaskId: string) {
    return this._http.delete<any>(
      `${this._removeEmployeeTask}?id=${empTaskId}`
    );
  }
  public AddEmployeeTask(payload: any) {
    return this._http.post<any>(`${this._addEmployeeTask}`, payload, {
      responseType: 'json',
    });
  }
  // TimeTracking

  public GetTimeRecordsByEmp(empId: string) {
    return this._http.get<any[]>(
      `${this._getTimeRecordsByEmp}?EmployeeId=${empId}`,
      {
        responseType: 'json',
      }
    );
  }
  public GetTimeRecordsByEmpAndStatus(empId: string) {
    return this._http.get<any[]>(
      `${this._getTimeRecordsByEmpAndStatus}?employeeId=${empId}&status=In Progress`,
      {
        responseType: 'json',
      }
    );
  }
  public GetTimeRecordsByTask(taskId: string) {
    return this._http.get<iTimeTrackingResponse[]>(
      `${this._getTimeRecordsByTask}?TaskId=${taskId}`,
      {
        responseType: 'json',
      }
    );
  }
  public GetLastTimeTrack(empId: string) {
    return this._http.get<any>(
      `${this._getLastTimeTrack}?EmployeeId=${empId}`,
      {
        responseType: 'json',
      }
    );
  }
  public GetTimeTrackByEmpAndTask(empId: string, taskId: string) {
    return this._http.get<any>(
      `${this._getTimeTrackByEmpAndTask}?EmployeeId=${empId}&TaskId=${taskId}`,
      {
        responseType: 'json',
      }
    );
  }
  public AddTimeTracking(payload: any) {
    return this._http.post<any>(this._addTimeTracking, payload, {
      responseType: 'json',
    });
  }
  public UpdateTimeTracking(payload: any) {
    return this._http.put<any>(this._updateTimeTracking, payload, {
      responseType: 'json',
    });
  }
  public RemoveTimeTracking(id: any) {
    return this._http.delete<any>(`${this._deleteTimeTracking}?id=${id}`, {
      responseType: 'json',
    });
  }

  public GetDueTasks(empId: string, today: boolean) {
    return this._http
      .post<iPaginationResult<iTaskResult>>(
        this._getAllDue,
        {
          empId,
          today,
          statuses: ['Pending', 'Assigned', 'In Progress', 'Paused'],
          potentialDelay: true,
          skipPagination: true,
          SortingParams: {},
        },
        { responseType: 'json' }
      )
      .pipe(
        map((x) => ({
          ...x,
          data: x.data.map((task) => {
            task.startDate = task.startDate
              ? format(task.startDate, 'MMM do, yyyy')
              : null;
            task.dueDate = format(task.dueDate!, 'MMM do, yyyy');
            return task;
          }),
        }))
      );
  }
  public GetTasksByEmployeeRange(ids: string[], sd: string, ed: string) {
    return this._http
      .post<Dictionary<Dictionary<Array<iWPTask>>>>(
        `${this._getTasksByEmployeeRange}?StartDate=${sd}&EndDate=${ed}`,
        ids,
        {
          responseType: 'json',
        }
      )
      .pipe(
        map((empTasks) => {
          for (const emp in empTasks) {
            if (Object.prototype.hasOwnProperty.call(empTasks, emp)) {
              const employee = empTasks[emp];
              for (const date in employee) {
                if (Object.prototype.hasOwnProperty.call(employee, date)) {
                  const tasks = employee[date];
                  empTasks[emp][date] = empTasks[emp][date].reverse();
                }
              }
            }
          }
          return empTasks;
        })
      );
  }
  public GetEmployeeTasksByStatus() {
    return this._http.post<any>(`${this._getEmployeeTaskStatus}`, null, {
      responseType: 'json',
    });
  }
  public CheckLoad(empId: string, taskId: string) {
    return this._http
      .get<any[]>(`${this._checkLoad}?EmployeeId=${empId}&TaskId=${taskId}`, {
        responseType: 'json',
      })
      .pipe(
        map((tasks) => {
          if (!tasks || !Array.isArray(tasks)) {
            return [];
          }
          tasks.forEach((task) => {
            if (task && task.dueDate) {
              task.dueDate = format(task.dueDate, 'do MMM, yyyy');
            }
          });
          return tasks;
        })
      );
  }

  getForManagementDashboard(
    LastStatusId: string,
    visibility: 'All' | 'Self' | 'Team'
  ) {
    const params = {
      LastStatusId,
      EmployeeId: visibility != 'All' ? this._dataServ.employeeValue.id : '',
      IsSubOrdinate: visibility == 'Self' ? false : true,
    };
    if (visibility == 'All') delete params.EmployeeId;
    return this._http
      .get<iTaskDashboardResponse[]>(`${this._getForManagementDashboard}`, {
        responseType: 'json',
        params,
      })
      .pipe(
        map((res) =>
          res.map((r) => ({
            ...r,
            startDate: formatDistanceToNow(r.createdDate, {
              addSuffix: true,
              includeSeconds: true,
            }),
          }))
        )
      );
  }
  getForNewTaskView(req: iTaskReq, self: boolean, employee: boolean = false) {
    return this._http
      .post<iPaginationResult<iTaskList>>(
        self || !req.empId || employee
          ? this._getForNewTaskView
          : this._getForNewTaskViewTeam,
        req,
        {
          responseType: 'json',
        }
      )
      .pipe(
        map((res) => {
          const data = res.data.map((x) => {
            const y: iTaskListDto = { ...x, due: 0, employeeNames: '' };
            if (y.dueDate) {
              y.due = differenceInDays(y.dueDate, Date.now());
              y.dueDate = format(y.dueDate, 'MMM dd, yyyy');
            }
            y.employees.forEach((z) => {
              if (z.profilePicture)
                z.profilePicture = `${environment.base_url}/${z.profilePicture}`;
            });
            if (y.startDate) y.startDate = format(y.startDate, 'MMM dd, yyyy');
            if (y.createdDate) {
              y.updatedDate = y.createdDate = format(
                y.createdDate,
                'MMM dd, yyyy'
              );
            }
            if (y.updatedDate)
              y.updatedDate = format(y.updatedDate, 'MMM dd, yyyy');
            return y;
          });
          return { ...res, data };
        })
      );
  }
  GetTasksInProgressByEmployee(employeeId: string) {
    return this._http.get<any[]>(
      `${this._getTasksInProgressByEmployee}/${employeeId}`,
      {
        responseType: 'json',
      }
    );
  }
  getPendingTaskView(req: iTaskReq, self: boolean, employee: boolean = false) {
    return this._http
      .post<iPaginationResult<iTaskList>>(this._getForPendingTaskView,
        req,
        {
          responseType: 'json',
        }
      )
      .pipe(
        map((res) => {
          const data = res.data.map((x) => {
            const y: iTaskListDto = { ...x, due: 0, employeeNames: '' };
            if (y.dueDate) {
              y.due = differenceInDays(y.dueDate, Date.now());
              y.dueDate = format(y.dueDate, 'MMM dd, yyyy');
            }
            y.employees.forEach((z) => {
              if (z.profilePicture)
                z.profilePicture = `${environment.base_url}/${z.profilePicture}`;
            });
            if (y.startDate) y.startDate = format(y.startDate, 'MMM dd, yyyy');
            if (y.createdDate) {
              y.updatedDate = y.createdDate = format(
                y.createdDate,
                'MMM dd, yyyy'
              );
            }
            if (y.updatedDate)
              y.updatedDate = format(y.updatedDate, 'MMM dd, yyyy');
            return y;
          });
          return { ...res, data };
        })
      );
  }
  processTimeTracking(records: iTimeTrackingResponse[]): iTimeTrackingEmp[] {
    return records
      .filter((x) => x.status == 'stop')
      .reduce((result, val) => {
        const r: iTimeTrackingResponseDto = {
          ...val,
          hours: differenceInMinutes(val.timeOut, val.timeIn) / 60,
        };
        const i = result.findIndex((e) => e.employeeId == val.employeeId);
        if (~i) result[i].hours += r.hours;
        else
          result.push({
            employee: r.employee,
            employeeId: r.employeeId,
            hours: r.hours,
          });
        return result;
      }, [] as iTimeTrackingEmp[]);
  }

  //#region Comments
  AddComments(payload: iCommentsRequest) {
    return this._http.post<iCommentsResult>(`${this._addComments}`, payload, {
      responseType: 'json',
    });
  }
  UpdateComments(payload: iCommentsRequest) {
    return this._http.put<iCommentsResult>(`${this._updateComments}`, payload, {
      responseType: 'json',
    });
  }
  //#endregion

  // public showModal$: Observable<Nullable<'init' | 'attendance'>>;
  // private _showModalSource: BehaviorSubject<Nullable<'init' | 'attendance'>>;
  // get showModalValue() {
  //   return this._showModalSource.getValue();
  // }
  // setShowModal(show: Nullable<'init' | 'attendance'>) {
  //   this._showModalSource.next(show);
  // }


  // TaskView
  AddTaskView(payload: iTaskViewReq) {
    return this._http.post<iTaskViewRes>(`${this._addTaskView}`, payload, {
      responseType: 'json',
    });
  }
  UpdateTaskView(payload: iTaskViewReq) {
    return this._http.put<iTaskViewRes>(`${this._updateTaskView}`, payload, {
      responseType: 'json',
    });
  }
  deleteTaskView(id: string) {
    return this._http.delete<boolean>(`${this._deleteTaskView}?id=${id}`);
  }
  public GetTaskViewByEmployeeId(id: string) {
    return this._http.get<iTaskViewRes[]>(`${this._getTaskViewByEmp}?Id=${id}`, {
      responseType: 'json',
    })
  }

  getTaskDescription(payload: any) {
    return this._http.post(this._getTaskDescription, payload, { responseType: 'json' });
  }

  generateTasks(payload: any) {
    return this._http.post(this._generateTasks, payload, { responseType: 'json' });
  }

  getEmployeeTaskSummary(payload: iEmployeeAttendanceSummaryRequest) {
    return this._http.post<iEmployeeAttendanceSummaryResponse[]>(
      this._getEmpTimeTracking,
      payload,
      {
        responseType: 'json',
      }
    );
  }
}
